import { createQuery } from "@/api";
import { QueryStringBuilder } from "@/utils";
import {
    ContextLineageMetricQueryResponse,
    createMetricQueryResponse,
    EvaluatedContextLineageMetricQueryResponse,
    EvaluatedLineageMetricQueryResponse,
    LineageMetricQueryResponse,
    MetricQueryResponseType,
    PlainMetricQueryResponse,
    ValueKind,
} from "./models/MetricQueryResponse.model";
import { MetricQueryString } from "./models/MetricQueryString.model";

export type MetricQueryParamsBase = {
    metricQuery: MetricQueryString;
    fromDate?: string;
    toDate?: string;
    valueKind?: ValueKind;
};

type MetricQueryParamsWithoutValueKind = Omit<
    MetricQueryParamsBase,
    "valueKind"
>;

const createMetricQueryFn =
    <T extends MetricQueryResponseType>(valueKind: ValueKind) =>
    async ({ router, params, api }: any) => {
        const query = new QueryStringBuilder()
            .add("value_kind", valueKind)
            .add("m", params.metricQuery.toString())
            .add("from", params.fromDate)
            .add("to", params.toDate)
            .build();

        const response = await api.get(
            `/finance/companies/${router.params.companyId}/metric-query?${query}`,
        );

        return createMetricQueryResponse(
            response,
            params.metricQuery,
            valueKind,
        ) as T;
    };

export const getMetricByQuery = createQuery<
    MetricQueryResponseType,
    MetricQueryParamsBase
>(async ({ router, params, api }) => {
    const valueKind = params.valueKind || ValueKind.PLAIN;
    return createMetricQueryFn(valueKind)({ router, params, api });
});

export const getPlainMetricByQuery = createQuery<
    PlainMetricQueryResponse,
    MetricQueryParamsWithoutValueKind
>(createMetricQueryFn<PlainMetricQueryResponse>(ValueKind.PLAIN));

export const getLineageMetricByQuery = createQuery<
    LineageMetricQueryResponse,
    MetricQueryParamsWithoutValueKind
>(createMetricQueryFn<LineageMetricQueryResponse>(ValueKind.LINEAGE));

export const getEvalLineageMetricByQuery = createQuery<
    EvaluatedLineageMetricQueryResponse,
    MetricQueryParamsWithoutValueKind
>(
    createMetricQueryFn<EvaluatedLineageMetricQueryResponse>(
        ValueKind.EVALED_LINEAGE,
    ),
);

export const getCtxLineageMetricByQuery = createQuery<
    ContextLineageMetricQueryResponse,
    MetricQueryParamsWithoutValueKind
>(
    createMetricQueryFn<ContextLineageMetricQueryResponse>(
        ValueKind.CTX_LINEAGE,
    ),
);

export const getEvalCtxLineageMetricByQuery = createQuery<
    EvaluatedContextLineageMetricQueryResponse,
    MetricQueryParamsWithoutValueKind
>(
    createMetricQueryFn<EvaluatedContextLineageMetricQueryResponse>(
        ValueKind.EVALED_CTX_LINEAGE,
    ),
);
